/**
 * TransferStopsTimeline.jsx
 * =========================
 * Timeline visuelle des étapes d'un transfert (contract_run).
 * - Mode lecture (client) : affiche progression sans actions.
 * - Mode gestion (admin/ops/chauffeur) : boutons Arrivé / En cours / Terminé / Passer.
 *
 * Props :
 *   transfer  : objet contract_run complet ({ stops, status, ... })
 *   canManage : bool — si true, affiche les boutons d'action
 *   onStopStatusChange : (stopId, newStatus) => Promise — appelé lors d'une action
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  MapPin, Package, PackageOpen, CheckCircle2, Clock, Truck, Circle,
  PlayCircle, SkipForward, Loader2,
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { icon: Circle, color: 'text-slate-400', bgColor: 'bg-slate-100', label: 'En attente' },
  en_route: { icon: Truck, color: 'text-blue-500', bgColor: 'bg-blue-100', label: 'En route' },
  arrived: { icon: MapPin, color: 'text-orange-500', bgColor: 'bg-orange-100', label: 'Arrivé' },
  in_progress: { icon: PlayCircle, color: 'text-purple-500', bgColor: 'bg-purple-100', label: 'En cours' },
  completed: { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-100', label: 'Terminé' },
  skipped: { icon: SkipForward, color: 'text-slate-300', bgColor: 'bg-slate-50', label: 'Passé' },
};

const STOP_TYPE_LABEL = {
  LOAD: { label: 'Chargement', icon: Package, color: 'bg-blue-100 text-blue-700' },
  DELIVERY: { label: 'Livraison', icon: PackageOpen, color: 'bg-emerald-100 text-emerald-700' },
  INTERMEDIATE: { label: 'Passage', icon: MapPin, color: 'bg-amber-100 text-amber-700' },
  TRANSFER: { label: 'Transfert', icon: Truck, color: 'bg-indigo-100 text-indigo-700' },
};

// Statuts du transfert qui autorisent encore des actions sur les stops
const ACTIVE_STATUSES = new Set([
  'planifiee', 'en_cours_de_chargement', 'en_transit', 'en_cours_de_dechargement', 'in_progress',
]);

const TransferStopsTimeline = ({ transfer, canManage = false, onStopStatusChange }) => {
  const [pendingStopId, setPendingStopId] = useState(null);

  if (!transfer) return null;
  const stops = (transfer.stops || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  if (stops.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6 text-sm text-slate-500 text-center">
          Aucune étape définie pour ce transfert.
        </CardContent>
      </Card>
    );
  }

  const isActive = ACTIVE_STATUSES.has((transfer.status || '').toLowerCase());

  const handleAction = async (stop, newStatus) => {
    if (!onStopStatusChange) return;
    setPendingStopId(stop.id);
    try {
      await onStopStatusChange(stop.id, newStatus);
    } finally {
      setPendingStopId(null);
    }
  };

  const renderActions = (stop) => {
    if (!canManage || !isActive) return null;
    const status = (stop.status || 'pending').toLowerCase();
    const buttons = [];
    if (status === 'pending') {
      buttons.push({ label: 'Marquer en route', next: 'en_route', icon: Truck });
    }
    if (status === 'en_route') {
      buttons.push({ label: 'Arrivé', next: 'arrived', icon: MapPin });
    }
    if (status === 'arrived') {
      buttons.push({ label: 'En cours', next: 'in_progress', icon: PlayCircle });
    }
    if (['arrived', 'in_progress'].includes(status)) {
      buttons.push({ label: 'Terminé', next: 'completed', icon: CheckCircle2 });
    }
    if (buttons.length === 0) return null;
    return (
      <div className="flex items-center gap-2 flex-wrap mt-2">
        {buttons.map((b) => {
          const Icon = b.icon;
          const loading = pendingStopId === stop.id;
          return (
            <Button
              key={b.next}
              size="sm"
              variant={b.variant || 'default'}
              disabled={loading}
              onClick={() => handleAction(stop, b.next)}
              className="h-8 px-3 text-xs"
              data-testid={`stop-${stop.id}-action-${b.next}`}
            >
              {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Icon className="w-3 h-3 mr-1" />}
              {b.label}
            </Button>
          );
        })}
      </div>
    );
  };

  const completedCount = stops.filter((s) => s.status === 'completed').length;

  return (
    <Card className="border-l-4 border-l-amber-500">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5 text-amber-500" />
            Progression du trajet
          </CardTitle>
          <Badge variant="outline" className="font-medium" data-testid="stops-progress-badge">
            {completedCount} / {stops.length} étapes terminées
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-slate-200" />
          <div className="space-y-1">
            {stops.map((stop, idx) => {
              const status = STATUS_CONFIG[stop.status] || STATUS_CONFIG.pending;
              const type = STOP_TYPE_LABEL[stop.stopType] || STOP_TYPE_LABEL.INTERMEDIATE;
              const StatusIcon = status.icon;
              const isFirst = idx === 0;
              const isLast = idx === stops.length - 1;
              const role = isFirst ? 'Départ' : isLast ? 'Arrivée' : `Étape ${idx}`;
              const arrivedAt = stop.timestamps?.arrivedAt || stop.arrivedAt;
              const completedAt = stop.timestamps?.completedAt || stop.completedAt;
              return (
                <div key={stop.id || idx} className="flex items-start gap-3 relative" data-testid={`stop-row-${idx}`}>
                  <div className={`relative z-10 p-1.5 rounded-full ${status.bgColor}`}>
                    <StatusIcon className={`h-4 w-4 ${status.color}`} />
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{role}</span>
                      <Badge className={`text-xs ${type.color}`}>{type.label}</Badge>
                      <Badge variant="outline" className="text-xs">{status.label}</Badge>
                    </div>
                    <p className="text-sm text-slate-700 mt-0.5 font-medium">
                      {stop.city || stop.address?.split(',')[0] || 'Adresse non renseignée'}
                    </p>
                    {stop.address && stop.address !== stop.city && (
                      <p className="text-xs text-slate-500 mt-0.5">{stop.address}</p>
                    )}
                    {stop.addressComplement && (
                      <p className="text-xs text-slate-500 mt-0.5 italic">{stop.addressComplement}</p>
                    )}
                    {stop.instructions && (
                      <p className="text-xs text-slate-500 mt-1 italic">"{stop.instructions}"</p>
                    )}
                    {arrivedAt && (
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> Arrivé : {new Date(arrivedAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                    {completedAt && (
                      <p className="text-xs text-emerald-600 mt-0.5 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Terminé : {new Date(completedAt).toLocaleString('fr-FR')}
                      </p>
                    )}
                    {renderActions(stop)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {transfer.distance_km && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Distance totale estimée :</span>
              <span className="font-semibold text-emerald-600">{Math.round(transfer.distance_km)} km</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TransferStopsTimeline;
